<template>
  <section id="reportes-resenas" class="review-reports dashboard-section">
    <div class="review-reports-header">
      <div>
        <h3>Reportes de reseñas</h3>
        <p>Revisa el caso antes de retirar una reseña. La resolución se envía por correo al profesional.</p>
      </div>
      <div class="review-reports-actions">
        <label for="review-report-status">Estado</label>
        <select id="review-report-status" v-model="status" :disabled="loading || saving !== null" @change="page = 1; load()">
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
          <option value="all">Todos</option>
        </select>
        <button type="button" :disabled="loading || saving !== null" @click="load">Actualizar</button>
      </div>
    </div>
    <p v-if="error" role="alert" class="review-reports-error">{{ error }}</p>
    <p v-if="loading" role="status">Cargando reportes…</p>
    <p v-else-if="!reports.length">No hay reportes en esta página.</p>
    <article v-for="item in reports" :key="item.id" class="review-report">
      <div class="review-reports-header">
        <strong>Reseña #{{ item.id }} · {{ item.profesional.nombre || 'Profesional' }} (#{{ item.profesional.id }})</strong>
        <span>{{ statusLabel(item.reporte.estado) }} · {{ formatDate(item.reporte.fecha_creado) }}</span>
      </div>
      <p><strong>Cliente:</strong> {{ item.cliente }} · {{ item.calificacion }}/5</p>
      <p class="review-report-text">{{ item.comentario || 'Sin comentario.' }}</p>
      <div v-if="item.original && (item.original.comentario !== item.comentario || item.original.calificacion !== item.calificacion)" class="review-report-original">
        <strong>Reseña al momento del reporte ({{ item.original.calificacion }}/5)</strong>
        <p class="review-report-text">{{ item.original.comentario || 'Sin comentario.' }}</p>
      </div>
      <p><strong>Motivo:</strong> {{ item.reporte.motivo_label }}</p>
      <p class="review-report-text">{{ item.reporte.detalle }}</p>
      <p v-if="item.oculto">Esta reseña ya está oculta.</p>
      <template v-if="item.reporte.estado === 'pendiente'">
        <label :for="'review-resolution-' + item.id">Resolución para el profesional</label>
        <textarea :id="'review-resolution-' + item.id" v-model="notes[item.id]" maxlength="2000" rows="3" :disabled="saving !== null" />
        <div class="review-reports-actions">
          <button type="button" :disabled="loading || saving !== null" @click="resolve(item, 'aprobado')">Aprobar y retirar reseña</button>
          <button type="button" :disabled="loading || saving !== null" @click="resolve(item, 'rechazado')">Rechazar reporte</button>
          <span v-if="saving === item.id" role="status">Guardando…</span>
        </div>
      </template>
      <p v-else class="review-report-text"><strong>Resolución:</strong> {{ item.reporte.resolucion }}</p>
    </article>
    <div class="review-reports-actions">
      <button type="button" :disabled="page <= 1 || loading || saving !== null" @click="page--; load()">Anterior</button>
      <span>Página {{ page }}</span>
      <button type="button" :disabled="!hasMore || loading || saving !== null" @click="page++; load()">Siguiente</button>
    </div>
    <div class="moderation-history">
      <button type="button" :disabled="saving !== null" @click="toggleHistory">{{ showHistory ? 'Ocultar historial de moderación' : 'Ver historial de moderación y correos' }}</button>
      <template v-if="showHistory">
        <h4>Historial de moderación</h4>
        <p v-if="historyLoading" role="status">Cargando historial…</p>
        <p v-if="historyError" role="alert" class="review-reports-error">{{ historyError }}</p>
        <article v-for="item in history" :key="item.id" class="review-report">
          <strong>Reseña #{{ item.calificacion_id }} · {{ item.profesional_nombre || 'Profesional' }}</strong>
          <p>{{ item.accion === 'retirada' ? 'Reseña retirada' : 'Reporte rechazado' }} · {{ formatDate(item.fecha) }} · Administrador #{{ item.admin_id }}</p>
          <p><strong>Motivo:</strong> {{ item.motivo_label }}</p>
          <p class="review-report-text">{{ item.resolucion }}</p>
          <p>{{ item.email_sent_at ? 'Correo enviado: ' + formatDate(item.email_sent_at) : 'Correo pendiente de envío' }}</p>
          <p v-if="item.email_error" class="review-reports-error">{{ item.email_error }}</p>
          <button v-if="!item.email_sent_at" type="button" :disabled="saving !== null" @click="retryEmail(item)">{{ saving === 'email-' + item.id ? 'Enviando…' : 'Reintentar correo' }}</button>
        </article>
        <p v-if="!historyLoading && !historyError && !history.length">No hay decisiones de moderación.</p>
        <div class="review-reports-actions">
          <button type="button" :disabled="historyPage <= 1 || historyLoading || saving !== null" @click="historyPage--; loadHistory()">Anterior</button>
          <span>Página {{ historyPage }}</span>
          <button type="button" :disabled="!historyHasMore || historyLoading || saving !== null" @click="historyPage++; loadHistory()">Siguiente</button>
          <button type="button" :disabled="historyLoading || saving !== null" @click="loadHistory">Actualizar historial</button>
        </div>
      </template>
    </div>

    <div v-if="removalTarget" class="review-removal-overlay" role="dialog" aria-modal="true" aria-labelledby="review-removal-title" @click.self="closeRemoval" @keydown.esc="closeRemoval">
      <form class="review-removal-modal" @submit.prevent="removeReview">
        <h3 id="review-removal-title">Retirar reseña #{{ removalTarget.id }}</h3>
        <p><strong>Profesional:</strong> {{ removalTarget.profesional_nombre }}</p>
        <p>{{ removalTarget.calificacion }}/5</p>
        <p class="review-report-text">{{ removalTarget.comentario || 'Sin comentario.' }}</p>
        <p>La reseña se retirará del perfil y dejará de contar en la calificación. Se conservará el historial y se enviará un correo al profesional.</p>
        <label for="review-removal-reason">Incumplimiento</label>
        <select id="review-removal-reason" v-model="removalReason" required :disabled="saving !== null">
          <option disabled value="">Selecciona un motivo</option>
          <option v-for="reason in removalReasons" :key="reason.id" :value="reason.id">{{ reason.label }}</option>
        </select>
        <label for="review-removal-resolution">Explicación para el profesional</label>
        <textarea id="review-removal-resolution" v-model="removalResolution" maxlength="2000" rows="4" required :disabled="saving !== null" />
        <p v-if="removalError" role="alert" class="review-reports-error">{{ removalError }}</p>
        <div class="review-reports-actions">
          <button type="button" :disabled="saving !== null" @click="closeRemoval">Cancelar</button>
          <button type="submit" class="review-removal-confirm" :disabled="saving !== null || loading || !removalReasons.length">{{ saving === 'remove' ? 'Retirando…' : 'Retirar y avisar al profesional' }}</button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReviewReports',
  data: () => ({ reports: [], status: 'pendiente', page: 1, hasMore: false, loading: false, saving: null, error: '', notes: {},
    removalTarget: null, removalReason: '', removalResolution: '', removalError: '', removalReasons: [],
    showHistory: false, history: [], historyPage: 1, historyHasMore: false, historyLoading: false, historyError: '' }),
  mounted() { this.load(); },
  methods: {
    url() { return `${window.hostname.replace(/\/$/, '')}/api/superadmin/review-reports`; },
    moderationUrl() { return `${window.hostname.replace(/\/$/, '')}/api/superadmin/review-moderations`; },
    statusLabel(status) { return { pendiente: 'Pendiente', aprobado: 'Aprobado', rechazado: 'Rechazado' }[status] || status; },
    formatDate(value) { return value ? new Date(value).toLocaleString('es-CL') : ''; },
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(this.url(), { params: { estado: this.status, page: this.page } });
        this.reports = data.reports;
        this.hasMore = data.has_more;
        this.removalReasons = data.removal_options?.reasons || [];
      } catch (error) {
        this.error = error.response?.data?.message || 'No pudimos cargar los reportes. Intenta actualizar.';
      } finally { this.loading = false; }
    },
    async resolve(item, estado) {
      if (this.saving !== null || this.loading) return;
      const resolucion = (this.notes[item.id] || '').trim();
      if (!resolucion || [...resolucion].length > 2000) {
        this.error = 'Escribe una resolución de entre 1 y 2000 caracteres.';
        return;
      }
      this.saving = item.id;
      this.error = '';
      try {
        const { data } = await axios.put(`${this.url()}/${item.id}`, { estado, resolucion });
        this.$delete(this.notes, item.id);
        this.notifyResult(data.moderacion);
        this.$emit('resolved');
        await this.load();
        if (this.showHistory) await this.loadHistory();
      } catch (error) {
        if (error.response?.status === 409) await this.load();
        this.error = error.response?.data?.message || 'No pudimos guardar la resolución. Intenta nuevamente.';
      } finally { this.saving = null; }
    },
    notifyResult(decision) {
      const sent = Boolean(decision?.email_sent_at);
      this.$notify({ group: 'foo', type: sent ? 'success' : 'warn', title: 'Moderación guardada',
        text: sent ? 'Se envió el correo al profesional.' : 'El correo quedó pendiente. Puedes reintentarlo en el historial de moderación.', duration: 8000 });
    },
    openRemoval(review) {
      if (this.saving !== null) return;
      this.removalTarget = review;
      this.removalReason = '';
      this.removalResolution = '';
      this.removalError = '';
      if (!this.removalReasons.length) this.load();
    },
    closeRemoval() { if (this.saving === null) this.removalTarget = null; },
    async removeReview() {
      if (this.saving !== null || this.loading || !this.removalTarget) return;
      const resolucion = this.removalResolution.trim();
      if (!this.removalReasons.some(reason => reason.id === this.removalReason) || !resolucion || [...resolucion].length > 2000) {
        this.removalError = 'Selecciona un motivo y escribe una explicación de entre 1 y 2000 caracteres.';
        return;
      }
      this.saving = 'remove';
      this.removalError = '';
      try {
        const { data } = await axios.post(`${window.hostname.replace(/\/$/, '')}/api/superadmin/reviews/${this.removalTarget.id}/remove`,
          { motivo: this.removalReason, resolucion });
        this.removalTarget = null;
        this.notifyResult(data.moderacion);
        this.$emit('resolved');
        await this.load();
        if (this.showHistory) await this.loadHistory();
      } catch (error) {
        this.removalError = error.response?.data?.message || 'No pudimos retirar la reseña. Intenta nuevamente.';
        if (error.response?.status === 409) this.$emit('resolved');
      } finally { this.saving = null; }
    },
    toggleHistory() { this.showHistory = !this.showHistory; if (this.showHistory) this.loadHistory(); },
    async loadHistory() {
      if (this.historyLoading) return;
      this.historyLoading = true;
      this.historyError = '';
      try {
        const { data } = await axios.get(this.moderationUrl(), { params: { page: this.historyPage } });
        this.history = data.items;
        this.historyHasMore = data.has_more;
      } catch (error) {
        this.historyError = error.response?.data?.message || 'No pudimos cargar el historial.';
      } finally { this.historyLoading = false; }
    },
    async retryEmail(item) {
      if (this.saving !== null) return;
      this.saving = 'email-' + item.id;
      try {
        const { data } = await axios.post(`${this.moderationUrl()}/${item.id}/email`);
        this.notifyResult(data.moderacion);
        await this.loadHistory();
      } catch (error) {
        this.historyError = error.response?.data?.message || 'No pudimos reintentar el correo.';
      } finally { this.saving = null; }
    },
  },
};
</script>

<style scoped>
.review-reports { grid-column: 1 / -1; padding: 20px; }
.review-reports h3 { margin: 0 0 8px; }
.review-reports-header, .review-reports-actions { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.review-reports-actions { justify-content: flex-start; }
.review-reports-header p { margin-bottom: 12px; }
.review-report { padding: 18px 0; margin: 8px 0; border-top: 1px solid #dbe3eb; }
.review-report-text { white-space: pre-wrap; overflow-wrap: anywhere; }
.review-report-original { background: #f1f5f9; padding: 12px; border-radius: 8px; }
.review-reports textarea { display: block; width: 100%; box-sizing: border-box; margin: 8px 0 12px; padding: 10px; }
.review-reports button, .review-reports select { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 6px; background: white; }
.review-reports button:disabled { opacity: 0.5; }
.review-reports-error { color: #b91c1c; }
.moderation-history { border-top: 1px solid #dbe3eb; margin-top: 20px; padding-top: 16px; }
.review-removal-overlay { position: fixed; inset: 0; z-index: 1100; background: rgba(15, 23, 42, .55); display: flex; align-items: center; justify-content: center; padding: 20px; }
.review-removal-modal { background: white; border-radius: 12px; padding: 24px; width: 600px; max-width: 100%; max-height: 90vh; overflow-y: auto; }
.review-removal-modal label { display: block; margin-top: 16px; }
.review-removal-modal select { width: 100%; margin: 8px 0 12px; }
.review-removal-confirm { color: #b91c1c; }
</style>
